import React, {useId, useRef, useState} from "react";
import {motion, useReducedMotion, type Variants} from "framer-motion";
import {useTheme} from "@mui/material";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {useMessages} from "i18n";
import {ModalSheet, SheetTitleAccent} from "components/ModalSheet";
import {brand, fontFamilies} from "theme/tokens";
import {GlanceCell, LaneTone, useGlanceRows, useHowItWorksLanes} from "./howItWorks";
import {HostedStage, P2pStage} from "./TransferStages";
import {FeaturesFaq} from "./FeaturesContent";
import {isPlainPrimaryClick} from "nav/resourceNavigation";
import styles from "./HowItWorksDialog.module.css";

/**
 * The how-it-works dialog (design `.hiw-modal`, ticket #45) - opened from the topbar's "Features"
 * link on the single-screen landing. Two lane cards (Instant P2P live stream / hosted files) with
 * animated mini-stages, the "At a glance" comparison table, and a "Start a transfer" CTA whose
 * native href and plain-click behavior both follow the owning-site navigation rule.
 *
 * Dumb view per epic #26: all copy lives in `howItWorks.ts`, the open/close state belongs to the
 * caller, and colors come from `theme.tokens` - bridged into `HowItWorksDialog.module.css` as
 * `--hiw-*` custom properties on {@link styles.root} so the CSS Module never carries a literal color
 * (owner decision 2026-07-12: no new styled-components; migrate reworked presentation to CSS Modules).
 *
 * ui#147 - scroll choreography: as each section / lane card / glance row scrolls into the sheet body
 * it rises + fades in (lane cards also scale-settle) via framer-motion `whileInView` (once). This is
 * IntersectionObserver-backed - no scroll listeners, no scroll-linked animation. Content above the
 * fold reveals on OPEN (it is already in view). Everything degrades to visible when
 * IntersectionObserver is unavailable, and reduces to a 120ms opacity-only fade under
 * `prefers-reduced-motion`.
 */

/** House reveal easing (matches the landing's rise entrance family). */
const EASE = [0.2, 0.8, 0.25, 1] as const;
/** Trigger reveals slightly before full entry so nothing pops in late; rows fire a touch tighter. */
const REVEAL_MARGIN = "0px 0px -12% 0px" as const;
const ROW_MARGIN = "0px 0px -6% 0px" as const;
const REVEAL_VIEWPORT = {once: true, margin: REVEAL_MARGIN} as const;

/** Bridge the active-mode design tokens into the CSS Module as `--hiw-*` custom properties. */
type CssVars = React.CSSProperties & Record<`--${string}`, string>;

function GlanceValue({cell, tone, includedLabel}: {cell: GlanceCell; tone: LaneTone; includedLabel: string}) {
	if ("check" in cell) {
		return <span className={styles.checkChip} data-tone={tone} aria-label={includedLabel}>✓</span>;
	}
	return cell.strong ? <b>{cell.text}</b> : <>{cell.text}</>;
}

type HowItWorksDialogProps = {
	open: boolean;
	onClose: () => void;
	/** Sole local focus-restoration owner when this dialog opens from a disappearing control. */
	onTransitionExited?: () => void;
	/** Optional section requested by an internal resource-dialog link. */
	focusTarget?: "faq" | null;
	/**
	 * The site ROUTE the CTA leads to, unresolved. {@link RouterLink} is the site-link seam, which turns
	 * it into the href this host needs — absolute on a recipient-only claimed/custom host (ui#432),
	 * inside the reader's locale tree on the main domain (ui#2498). A caller that resolved it first
	 * would have it resolved twice.
	 */
	startTransferPath: string;
	/** The "Start a transfer" CTA; callers close the sheet and navigate through the site-link rule. */
	onStartTransfer: () => void;
};

export function HowItWorksDialog({
	open, onClose, onTransitionExited, startTransferPath, onStartTransfer, focusTarget = null,
}: HowItWorksDialogProps) {
	const {features} = useMessages();
	const {dialog, page, laneNames} = features;
	const lanes = useHowItWorksLanes();
	const glanceRows = useGlanceRows();
	const {tokens} = useTheme();
	const reduce = useReducedMotion();
	const faqHeadingId = useId();
	const faqHeadingRef = useRef<HTMLHeadingElement>(null);
	// Resolved once at mount (client-only - the sheet only renders when open). When IntersectionObserver
	// is missing the reveals never arm, so content renders at its natural (visible) state - no permanent
	// invisibility, no first-paint flash.
	const [io] = useState(() => typeof IntersectionObserver !== "undefined");
	const focusRequestedTarget = () => {
		if (focusTarget !== "faq") return;
		faqHeadingRef.current?.scrollIntoView({block: "start"});
		faqHeadingRef.current?.focus({preventScroll: true});
	};
	const onStartTransferClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		if (!isPlainPrimaryClick(event)) return;
		event.preventDefault();
		onStartTransfer();
	};

	const cssVars: CssVars = {
		"--hiw-ink": tokens.ink,
		"--hiw-ink2": tokens.ink2,
		"--hiw-blue": tokens.blue,
		"--hiw-blued": tokens.blued,
		"--hiw-teal": tokens.teal,
		// Accessible text inks per lane tone (segInk/signalInk): the vivid blue/teal accents fail WCAG
		// AA as small badge/glyph text, so text uses these while borders/dots keep the vivid accent.
		"--hiw-blue-ink": tokens.segInk,
		"--hiw-teal-ink": tokens.signalInk,
		"--hiw-card": tokens.card,
		"--hiw-card-border": tokens.cardBorder,
		"--hiw-line": tokens.line,
		"--hiw-accent-tint": tokens.accentTint,
		"--hiw-signal-tint": tokens.signalTint,
		"--hiw-on-accent": brand.white,
		"--hiw-font-sans": fontFamilies.sans,
		"--hiw-font-mono": fontFamilies.mono,
	};

	// Reveal variants - transforms + stagger under full motion, opacity-only 120ms under reduced motion.
	const groupVariants: Variants = {
		hidden: {},
		visible: {transition: {staggerChildren: reduce ? 0 : 0.06, delayChildren: reduce ? 0 : 0.04}},
	};
	const cardVariants: Variants = reduce
		? {hidden: {opacity: 0}, visible: {opacity: 1, transition: {duration: 0.12}}}
		: {hidden: {opacity: 0, y: 16, scale: 0.985}, visible: {opacity: 1, y: 0, scale: 1, transition: {duration: 0.5, ease: EASE}}};
	const itemVariants: Variants = reduce
		? {hidden: {opacity: 0}, visible: {opacity: 1, transition: {duration: 0.12}}}
		: {hidden: {opacity: 0, y: 14}, visible: {opacity: 1, y: 0, transition: {duration: 0.42, ease: EASE}}};

	// Per-block reveal (CTA + each glance row observes itself as it scrolls in).
	const blockInitial = reduce ? {opacity: 0} : {opacity: 0, y: 14};
	const blockInView = reduce ? {opacity: 1} : {opacity: 1, y: 0};
	const blockTransition = {duration: reduce ? 0.12 : 0.42, ease: EASE};
	const rowInitial = reduce ? {opacity: 0} : {opacity: 0, y: 10};
	const rowInView = reduce ? {opacity: 1} : {opacity: 1, y: 0};
	const rowTransition = {duration: reduce ? 0.12 : 0.34, ease: EASE};

	// A single glance row - a `motion.tr` that reveals as it enters the sheet body while scrolling.
	const renderGlanceRow = (label: string, cells: React.ReactNode) => (
		<motion.tr
			key={label}
			initial={io ? rowInitial : false}
			whileInView={io ? rowInView : undefined}
			viewport={{once: true, margin: ROW_MARGIN}}
			transition={rowTransition}
		>
			{cells}
		</motion.tr>
	);

	return (
		<ModalSheet
			open={open}
			onClose={onClose}
			onTransitionEntered={focusRequestedTarget}
			onTransitionExited={onTransitionExited}
			ariaLabel={dialog.ariaLabel}
			eyebrow={dialog.eyebrow}
			title={<>{dialog.titleLead}<SheetTitleAccent>{dialog.titleAccent}</SheetTitleAccent></>}
			lede={dialog.lede}
			width={980}
		>
			<div className={styles.root} style={cssVars}>
				<motion.div
					className={styles.grid}
					variants={groupVariants}
					initial={io ? "hidden" : false}
					whileInView={io ? "visible" : undefined}
					viewport={REVEAL_VIEWPORT}
				>
					{lanes.map((lane) => (
						<motion.article key={lane.id} className={styles.card} data-tone={lane.tone} variants={cardVariants}>
							{lane.id === "p2p" ? <P2pStage/> : <HostedStage/>}
							<div className={styles.badgeRow}>
								<span className={styles.badge}>{lane.badge}</span>
								<span className={styles.ghost}>{lane.ghost}</span>
							</div>
							<h3 className={styles.title}>{lane.title}</h3>
							<p className={styles.desc}>{lane.description}</p>
							<ul className={styles.bullets}>
								{lane.bullets.map((bullet) => (
									<li key={bullet}>
										<span className={styles.check} aria-hidden="true">✓</span>
										{bullet}
									</li>
								))}
							</ul>
							<div className={styles.foot}>
								<span>{page.bestFor}</span>
								<b>{lane.bestFor}</b>
							</div>
						</motion.article>
					))}
				</motion.div>

				<motion.div
					variants={groupVariants}
					initial={io ? "hidden" : false}
					whileInView={io ? "visible" : undefined}
					viewport={REVEAL_VIEWPORT}
				>
					<motion.h3 className={styles.glanceHeading} variants={itemVariants}>{page.glanceHeading}</motion.h3>
					<motion.p className={styles.glanceSub} variants={itemVariants}>{page.glanceSub}</motion.p>
				</motion.div>

				<div className={styles.glanceWrap}>
					<table className={styles.glanceTable}>
						<thead>
							{renderGlanceRow(
								"__head",
								<>
									<th aria-hidden="true"/>
									<th><span className={styles.glanceLane} data-tone="blue">{laneNames.p2p}</span></th>
									<th><span className={styles.glanceLane} data-tone="teal">{laneNames.hosted}</span></th>
								</>,
							)}
						</thead>
						<tbody>
							{glanceRows.map((row) => renderGlanceRow(
								row.label,
								<>
									<td>{row.label}</td>
									<td><GlanceValue cell={row.p2p} tone="blue" includedLabel={page.included}/></td>
									<td><GlanceValue cell={row.hosted} tone="teal" includedLabel={page.included}/></td>
								</>,
							))}
						</tbody>
					</table>
				</div>

				<FeaturesFaq
					headingId={faqHeadingId}
					headingRef={faqHeadingRef}
					focusableHeading={focusTarget === "faq"}
				/>

				<motion.div
					className={styles.ctaRow}
					initial={io ? blockInitial : false}
					whileInView={io ? blockInView : undefined}
					viewport={REVEAL_VIEWPORT}
					transition={blockTransition}
				>
					<span className={styles.ctaNote}>{dialog.ctaNote}</span>
					<RouterLink className={styles.ctaButton} to={startTransferPath} onClick={onStartTransferClick}>
						{dialog.cta}
					</RouterLink>
				</motion.div>
			</div>
		</ModalSheet>
	);
}
